import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useMutation, UseMutationResult } from 'react-query';
import { useIntercom } from 'react-use-intercom';
import { api } from '../api';
import { asyncToast } from '../components/toast/Toast';
import { intercomEvents } from '../intercom-events';
import queryClient from '../query-client';
import { FileRecord } from '../types/file-record';

type CreateDocumentRequest = {
    file: File;
    parentFolderId: string;
};

export type DocumentRecordRequest = { name: string; type: string; size: number; parentFolderId: string };

async function createDocument({ file, parentFolderId }: CreateDocumentRequest): Promise<FileRecord> {
    const isMock = process.env.REACT_APP_MOCK_USER === 'true';
    const { data } = await api.post('/file-records/documents', {
        name: file.name,
        type: file.type,
        size: file.size,
        parentFolderId,
    });

    try {
        // Special use case where we need to use the default axios instance to upload to s3
        await axios.put(data.upload.url, file, {
            headers: {
                'Content-Type': isMock ? 'application/octet-stream' : 'multipart/form-data',
            },
        });
        return data.record;
    } catch (error) {
        // On any network error, delete the file record
        await api.delete('/file-records', { data: [{ id: data.record.id, keepReferences: false }] });
        // Record a Sentry event to find common file upload failures
        Sentry.captureMessage(`Failed to upload document. Bytes: ${file.size} | Type: ${file.type}`, 'warning');

        throw new Error('Unable to upload the document. Please try again');
    }
}

export function useCreateDocument(): UseMutationResult<FileRecord, unknown, CreateDocumentRequest, unknown> {
    const { trackEvent } = useIntercom();
    return useMutation((req) => createDocument(req), {
        onMutate: (req) => {
            return asyncToast(`Uploading ${req.file.name}...`);
        },
        onSettled: () => {
            queryClient.invalidateQueries(['file-records']);
        },
        onError: (_, { file }, done) => {
            done && done('error', { title: `Unable to upload ${file.name}` });
        },
        onSuccess: (_, { file }, done) => {
            trackEvent(intercomEvents.file.document.event);
            done && done('success', { title: `Successfully uploaded ${file.name}` });
        },
    });
}
